import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { NavBar } from '../components/NavBar';
import { Button } from '../components/ui/button';
import { 
  Crown, 
  Check, 
  Loader2, 
  Star,
  Zap,
  Shield,
  Trophy,
  Dumbbell,
  Utensils,
  MessageSquare,
  Pause,
  Play,
  X
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export const SubscriptionPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { api, user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [canceling, setCanceling] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [pauseStep, setPauseStep] = useState('reason'); // 'reason' | 'duration'
  const [pauseReason, setPauseReason] = useState(null);
  const [pauseReasonNote, setPauseReasonNote] = useState('');
  const [pauseMonths, setPauseMonths] = useState(1);
  const [pausing, setPausing] = useState(false);
  const [resuming, setResuming] = useState(false);

  useEffect(() => {
    fetchData();
    
    // Check for success redirect
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      verifySubscription(sessionId);
    }
  }, []);

  const fetchData = async () => {
    try {
      const [plansRes, statusRes] = await Promise.all([
        api().get('/subscriptions/plans'),
        api().get('/subscriptions/status')
      ]);
      
      setPlans(plansRes.data.plans || []);
      setSubscription(statusRes.data.subscription);
      setIsSubscribed(statusRes.data.is_subscribed);
    } catch (err) {
      toast.error('Failed to load subscription data');
    }
    setLoading(false);
  };

  const verifySubscription = async (sessionId) => {
    setLoading(true);
    try {
      const res = await api().post(`/subscriptions/verify?session_id=${sessionId}`);
      
      if (res.data.success) {
        toast.success('Subscription activated! Welcome to Premium!');
        await fetchData();
        if (refreshUser) await refreshUser();
        // Clear URL params
        navigate('/subscription', { replace: true });
      } else {
        toast.error(res.data.message || 'Verification failed');
      }
    } catch (err) {
      toast.error('Failed to verify subscription');
    }
    setLoading(false);
  };

  const handleSubscribe = async (planId) => {
    setProcessingPlan(planId);
    try {
      const res = await api().post(`/subscriptions/create-checkout?plan_id=${planId}`);
      
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
      setProcessingPlan(null);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will still have access until the end of your billing period.')) {
      return;
    }
    
    setCanceling(true);
    try {
      const res = await api().post('/subscriptions/cancel');
      
      if (res.data.success) {
        toast.success('Subscription will be canceled at end of billing period');
        await fetchData();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel subscription');
    }
    setCanceling(false);
  };

  const handleReactivate = async () => {
    setReactivating(true);
    try {
      const res = await api().post('/subscriptions/reactivate');
      
      if (res.data.success) {
        toast.success('Subscription reactivated!');
        await fetchData();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reactivate');
    }
    setReactivating(false);
  };

  const handlePause = async () => {
    setPausing(true);
    try {
      const params = new URLSearchParams({ months: String(pauseMonths) });
      if (pauseReason) params.append('reason', pauseReason);
      if (pauseReason === 'other' && pauseReasonNote.trim()) {
        params.append('reason_note', pauseReasonNote.trim());
      }
      const res = await api().post(`/subscriptions/pause?${params.toString()}`);
      if (res.data.success) {
        toast.success(res.data.message);
        closePauseModal();
        await fetchData();
        if (refreshUser) await refreshUser();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to pause subscription');
    }
    setPausing(false);
  };

  const openPauseModal = () => {
    setPauseStep('reason');
    setPauseReason(null);
    setPauseReasonNote('');
    setPauseMonths(1);
    setShowPauseModal(true);
  };

  const closePauseModal = () => {
    setShowPauseModal(false);
    // Defer state reset so it doesn't flash during the exit animation
    setTimeout(() => {
      setPauseStep('reason');
      setPauseReason(null);
      setPauseReasonNote('');
    }, 200);
  };

  const handleResume = async () => {
    setResuming(true);
    try {
      const res = await api().post('/subscriptions/resume');
      if (res.data.success) {
        toast.success(res.data.message);
        await fetchData();
        if (refreshUser) await refreshUser();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resume subscription');
    }
    setResuming(false);
  };

  const handleManageBilling = async () => {
    try {
      const res = await api().get('/subscriptions/billing-portal');
      
      if (res.data.portal_url) {
        window.location.href = res.data.portal_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to open billing portal');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const featureIcons = {
    'All workout programs unlocked': Dumbbell,
    'Customized meal plans': Utensils,
    'Advanced progress tracking': Trophy,
    'Priority messaging with trainers': MessageSquare,
    'Exclusive challenges': Star,
    'Ad-free experience': Shield,
    '2 months FREE': Zap
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black">
        <NavBar title="Premium" />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 text-primary animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="subscription-page">
      <NavBar title="Premium" />

      <main className="container mx-auto px-6 py-8 pb-20">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 mb-4">
            <Crown className="h-10 w-10 text-primary" />
            <h1 className="font-heading text-4xl md:text-5xl text-white uppercase tracking-wider">
              Go <span className="text-primary">Premium</span>
            </h1>
          </div>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Unlock your full potential with unlimited access to all workout programs, 
            personalized meal plans, and exclusive features.
          </p>
        </motion.div>

        {/* Current Subscription Status */}
        {isSubscribed && subscription && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl mx-auto mb-12 bg-gradient-to-r from-primary/20 to-primary/5 border border-primary/30 p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <Crown className="h-8 w-8 text-primary" />
              <div>
                <h3 className="font-heading text-xl text-white uppercase">You're Premium!</h3>
                <p className="text-muted-foreground">
                  {subscription.plan?.name || 'Premium Plan'}
                </p>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="bg-black/30 p-4 rounded">
                <p className="text-sm text-muted-foreground">Current Period</p>
                <p className="text-white">
                  {formatDate(subscription.current_period_start)} - {formatDate(subscription.current_period_end)}
                </p>
              </div>
              <div className="bg-black/30 p-4 rounded">
                <p className="text-sm text-muted-foreground">Status</p>
                {subscription.status === 'paused' ? (
                  <p className="font-medium text-blue-400" data-testid="subscription-status-paused">
                    Paused {subscription.paused_until ? `until ${formatDate(subscription.paused_until)}` : ''}
                  </p>
                ) : (
                  <p className={`font-medium ${subscription.cancel_at_period_end ? 'text-yellow-400' : 'text-green-400'}`}>
                    {subscription.cancel_at_period_end ? 'Cancels at period end' : 'Active'}
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex flex-wrap gap-3">
              {subscription.status === 'paused' ? (
                <Button
                  onClick={handleResume}
                  disabled={resuming}
                  className="bg-primary text-black"
                  data-testid="resume-subscription-btn"
                >
                  {resuming ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
                  Resume Subscription
                </Button>
              ) : subscription.cancel_at_period_end ? (
                <Button
                  onClick={handleReactivate}
                  disabled={reactivating}
                  className="bg-primary text-black"
                >
                  {reactivating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Reactivate Subscription
                </Button>
              ) : (
                <>
                  {!subscription.is_legacy_one_time && (
                    <Button
                      onClick={openPauseModal}
                      variant="outline"
                      className="border-blue-500 text-blue-400 hover:bg-blue-500/10"
                      data-testid="pause-subscription-btn"
                    >
                      <Pause className="h-4 w-4 mr-2" />
                      Pause Subscription
                    </Button>
                  )}
                  <Button
                    onClick={handleCancel}
                    disabled={canceling}
                    variant="outline"
                    className="border-red-500 text-red-400 hover:bg-red-500/10"
                    data-testid="cancel-subscription-btn"
                  >
                    {canceling ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Cancel Subscription
                  </Button>
                </>
              )}
              <Button
                onClick={handleManageBilling}
                variant="outline"
                className="border-border"
              >
                Manage Billing
              </Button>
            </div>
          </motion.div>
        )}

        {/* Pause Confirmation Modal — 2 steps: reason → duration */}
        <AnimatePresence>
          {showPauseModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4"
              onClick={() => !pausing && closePauseModal()}
              data-testid="pause-subscription-modal"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="max-w-md w-full bg-black border-2 border-blue-500/40 p-6"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <Pause className="h-6 w-6 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl text-white uppercase tracking-wider">
                      {pauseStep === 'reason' ? 'Pause, don\'t quit' : 'How long?'}
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      {pauseStep === 'reason' ? 'Quick — what\'s the reason?' : `Pause for up to 3 months`}
                    </p>
                  </div>
                </div>

                {pauseStep === 'reason' ? (
                  <>
                    <p className="text-sm text-muted-foreground mb-5">
                      Help us understand why so we can make the app better. Totally optional.
                    </p>
                    <div className="space-y-2 mb-4" data-testid="pause-reason-options">
                      {[
                        { id: 'vacation', label: 'Going on vacation', sub: 'Travel, holidays, time off' },
                        { id: 'injury', label: 'Injured / sick', sub: 'Need to heal up first' },
                        { id: 'cost', label: 'Too expensive right now', sub: 'Budget reasons' },
                        { id: 'not_using', label: 'Not using it enough', sub: 'Need to rebuild the habit' },
                        { id: 'other', label: 'Something else', sub: 'Tell us in a sec' },
                      ].map((r) => (
                        <button
                          key={r.id}
                          onClick={() => setPauseReason(r.id)}
                          className={`w-full text-left p-3 border transition-colors ${
                            pauseReason === r.id
                              ? 'border-blue-500 bg-blue-500/10 text-white'
                              : 'border-border text-muted-foreground hover:border-blue-500/40'
                          }`}
                          data-testid={`pause-reason-${r.id}`}
                        >
                          <div className="font-medium text-white">{r.label}</div>
                          <div className="text-xs opacity-60">{r.sub}</div>
                        </button>
                      ))}
                    </div>

                    {pauseReason === 'other' && (
                      <textarea
                        value={pauseReasonNote}
                        onChange={(e) => setPauseReasonNote(e.target.value.slice(0, 500))}
                        placeholder="What's up? (optional, max 500 chars)"
                        rows={3}
                        className="w-full bg-black/60 border border-blue-500/40 p-3 text-white text-sm mb-4 focus:outline-none focus:border-blue-500"
                        data-testid="pause-reason-note"
                      />
                    )}

                    <div className="flex gap-2">
                      <Button
                        onClick={closePauseModal}
                        variant="outline"
                        className="flex-1"
                        data-testid="pause-cancel-btn"
                      >
                        Never mind
                      </Button>
                      <Button
                        onClick={() => setPauseStep('duration')}
                        className="flex-1 bg-blue-500 text-white hover:bg-blue-600"
                        data-testid="pause-reason-next-btn"
                      >
                        Continue
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground mb-5">
                      You won't be charged, but you'll lose premium access. Your account, AI plans,
                      logs, social graph, and progress photos all stay safe.
                    </p>
                    <div className="space-y-2 mb-6" data-testid="pause-duration-options">
                      {[1, 2, 3].map((m) => (
                        <button
                          key={m}
                          onClick={() => setPauseMonths(m)}
                          className={`w-full text-left p-3 border transition-colors ${
                            pauseMonths === m
                              ? 'border-blue-500 bg-blue-500/10 text-white'
                              : 'border-border text-muted-foreground hover:border-blue-500/40'
                          }`}
                          data-testid={`pause-duration-${m}`}
                        >
                          <span className="font-medium">{m} month{m > 1 ? 's' : ''}</span>
                          <span className="text-xs ml-2 opacity-60">
                            — resume {new Date(Date.now() + m * 30 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        </button>
                      ))}
                    </div>

                    <div className="flex gap-2">
                      <Button
                        onClick={() => setPauseStep('reason')}
                        variant="outline"
                        className="flex-1"
                        disabled={pausing}
                        data-testid="pause-back-btn"
                      >
                        Back
                      </Button>
                      <Button
                        onClick={handlePause}
                        disabled={pausing}
                        className="flex-1 bg-blue-500 text-white hover:bg-blue-600"
                        data-testid="pause-confirm-btn"
                      >
                        {pausing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Pause className="h-4 w-4 mr-2" />}
                        Pause for {pauseMonths} month{pauseMonths > 1 ? 's' : ''}
                      </Button>
                    </div>
                  </>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Pricing Plans */}
        {!isSubscribed && (
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {plans.map((plan, index) => {
              const isYearly = plan.id === 'yearly';
              const savings = isYearly ? Math.round((9.99 * 12 - plan.price) / (9.99 * 12) * 100) : 0;
              
              return (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`relative bg-card border ${
                    isYearly ? 'border-primary' : 'border-border'
                  } p-6 flex flex-col`}
                >
                  {isYearly && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span className="bg-primary text-black px-4 py-1 text-sm font-bold uppercase">
                        Best Value
                      </span>
                    </div>
                  )}
                  
                  <div className="text-center mb-6">
                    <h3 className="font-heading text-2xl text-white uppercase tracking-wider mb-2">
                      {plan.name}
                    </h3>
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="font-heading text-5xl text-primary">
                        ${plan.price.toFixed(2)}
                      </span>
                      <span className="text-muted-foreground">
                        /{plan.interval}
                      </span>
                    </div>
                    {isYearly && (
                      <p className="text-green-400 text-sm mt-2">
                        Save {savings}% vs monthly
                      </p>
                    )}
                    <p className="text-muted-foreground text-sm mt-2">
                      {plan.description}
                    </p>
                  </div>
                  
                  <ul className="space-y-3 mb-8 flex-1">
                    {plan.features.map((feature) => {
                      const Icon = featureIcons[feature] || Check;
                      return (
                        <li key={`${plan.id}-${feature}`} className="flex items-center gap-3 text-white">
                          <Icon className="h-5 w-5 text-primary flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      );
                    })}
                  </ul>
                  
                  <Button
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={processingPlan !== null}
                    className={`w-full h-12 text-lg ${
                      isYearly 
                        ? 'bg-primary text-black hover:bg-primary/90' 
                        : 'bg-white/10 text-white hover:bg-white/20'
                    }`}
                    data-testid={`subscribe-${plan.id}-btn`}
                  >
                    {processingPlan === plan.id ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin mr-2" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Crown className="h-5 w-5 mr-2" />
                        Subscribe Now
                      </>
                    )}
                  </Button>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Benefits Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-16 text-center"
        >
          <h2 className="font-heading text-2xl text-white uppercase tracking-wider mb-8">
            Why Go Premium?
          </h2>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="bg-card border border-border p-6">
              <Dumbbell className="h-10 w-10 text-primary mx-auto mb-4" />
              <h3 className="font-heading text-lg text-white uppercase mb-2">Select Programs</h3>
              <p className="text-muted-foreground text-sm">
                Access challenges and select workout programs included with your membership
              </p>
            </div>
            <div className="bg-card border border-border p-6">
              <Utensils className="h-10 w-10 text-primary mx-auto mb-4" />
              <h3 className="font-heading text-lg text-white uppercase mb-2">Custom Meals</h3>
              <p className="text-muted-foreground text-sm">
                Personalized meal plans tailored to your goals
              </p>
            </div>
            <div className="bg-card border border-border p-6">
              <MessageSquare className="h-10 w-10 text-primary mx-auto mb-4" />
              <h3 className="font-heading text-lg text-white uppercase mb-2">Priority Support</h3>
              <p className="text-muted-foreground text-sm">
                Direct messaging with professional trainers
              </p>
            </div>
          </div>
        </motion.div>

        {/* FAQ */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-16 max-w-2xl mx-auto"
        >
          <h2 className="font-heading text-2xl text-white uppercase tracking-wider text-center mb-8">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            <div className="bg-card border border-border p-4">
              <h3 className="font-medium text-white mb-2">Can I cancel anytime?</h3>
              <p className="text-muted-foreground text-sm">
                Yes! You can cancel your subscription anytime. You'll continue to have access 
                until the end of your current billing period.
              </p>
            </div>
            <div className="bg-card border border-border p-4">
              <h3 className="font-medium text-white mb-2">What payment methods do you accept?</h3>
              <p className="text-muted-foreground text-sm">
                We accept all major credit cards through our secure Stripe payment processor.
              </p>
            </div>
            <div className="bg-card border border-border p-4">
              <h3 className="font-medium text-white mb-2">Can I switch between plans?</h3>
              <p className="text-muted-foreground text-sm">
                Yes, you can upgrade or downgrade your plan at any time through the billing portal.
              </p>
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default SubscriptionPage;
